import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { useNotices } from '@/features/notices/useNotices';
import { generateInvitationLink } from '@/features/vfo/helpers';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { FormItem } from '@/features/vfo/uikit/Form';
import { useApi } from '@/shared/api';
import { breakpoints, Input, media } from '@/shared/components';
import { EMAIL_REGEX } from '@/shared/lib/hooks/use-form-render/helpers';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import { useProfileStore } from '@/shared/store/profile.store';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { ResendInvitationModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../../interwill/containers/ModalContainerV1';
import { InterwillStyledForm } from '../../interwill/CreateEntityModal/styled';

type FormValues = {
  email: string;
};

export const ResendInvitationModal: FC<ResendInvitationModalProps> = ({ id }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'resendInvitation' });
  const sjDb = useSJDatabase();

  const notices = useNotices();
  const { firstName, lastName } = useProfileStore();
  const { members, fetchMembers } = useMembers();
  const modals = useModalsStore();
  const api = useApi();
  const member = members.find((m) => m.invitationId === id);

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      email: member?.email ?? '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    const beneficiary = sjDb.collections.beneficiaries?.findOne((b) => b.email === values.email);

    if (!member?.tag) return;
    const shouldResend = values.email === member.email;

    if (!shouldResend && !beneficiary && member.invitationId) {
      const { error } = await api.vfoInvitationDelete(member.invitationId);

      if (error) {
        notices.error('Failed to resend invitation');
        return;
      }
    }

    if (beneficiary) {
      const { data: scopesData } = await api.scopesGetAll();
      const currentScope = scopesData?.find(
        (scope) => scope.profileId === member.profileId && scope.scopeType === 'VFO',
      );
      if (!currentScope) return;

      const { error: deletionError } = await api.scopesDelete(currentScope.id);
      const { error: scopeError } = await api.scopeCreate({
        profileId: beneficiary.centralDbProfileId,
        scopeType: 'VFO',
        tag: member.tag,
      });

      if (deletionError || scopeError) {
        notices.error('Failed to resend invitation');
      }

      modals.closeModal();
      return;
    }

    const { data: invitationData } =
      shouldResend && member.invitationId
        ? await api.vfoInvitationResend(member.invitationId)
        : await api.vfoInviteMember({
            email: values.email,
            tag: member.tag,
            firstName: member.firstName,
            lastName: member.lastName,
          });

    if (!invitationData || !firstName || !lastName || !member.tag) {
      notices.error('Failed to resend invitation');
      return;
    }

    modals.closeModal();
    modals.openModal({
      type: 'InvitationSuccessModal',
      invitationLink: generateInvitationLink({
        token: invitationData.invitationToken ?? '',
        email: invitationData.email,
        tag: member.tag,
        tenantProfileFirstName: firstName,
        tenantProfileLastName: lastName,
        firstName: invitationData.firstName ?? '',
        lastName: invitationData.lastName ?? '',
      }),
      sentEmail: values.email,
    });
    fetchMembers();
  };

  if (!member) {
    return null;
  }

  const memberFullName =
    member.firstName && member.lastName
      ? `${member.firstName} ${member.lastName}`
      : t('invitedMember');

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm style={{ height: '100%' }} onFinish={formHandleSubmit(onSubmit)}>
        <InnerForm>
          <span>
            {t('instructionText')} <strong>{memberFullName}</strong>
          </span>

          <Controller
            name="email"
            control={control}
            rules={{
              required: t('required'),
              pattern: EMAIL_REGEX,
            }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t('emailLabel')} required>
                  <FormItem
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input placeholder={t('emailPlaceholder')} type="text" {...field} />
                  </FormItem>
                </FieldContainerIw>
              );
            }}
          />
        </InnerForm>

        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('sendBtn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-size: 14px;
  height: 100%;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column;
    margin-top: auto;
  }
`;
